"use client";

import { motion } from "framer-motion";
import clsx from "clsx";

interface BackgroundCirclesProps {
    className?: string;
    variant?: keyof typeof COLOR_VARIANTS;
}

const COLOR_VARIANTS = {
    orange: {
        border: [
            "border-orange-500/60",
            "border-yellow-400/50",
            "border-rose-600/30",
        ],
        gradient: "from-orange-500/30",
    },
    sunset: {
        border: [
            "border-orange-600/60",
            "border-pink-400/50",
            "border-rose-600/30",
        ],
        gradient: "from-orange-600/30",
    },
} as const;

const AnimatedGrid = () => (
    <motion.div
        className="absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,transparent_30%,black)]"
        animate={{
            backgroundPosition: ["0% 0%", "100% 100%"],
        }}
        transition={{
            duration: 40,
            repeat: Number.POSITIVE_INFINITY,
            ease: "linear",
        }}
    >
        {/* Clean subtle pattern without numbers */}
        <div className="h-full w-full opacity-5 bg-gradient-to-br from-orange-200/30 to-transparent" />
    </motion.div>
);

export function BackgroundCircles({
    className,
    variant = "orange",
}: BackgroundCirclesProps) {
    const variantStyles = COLOR_VARIANTS[variant];

    return (
        <div
            className={clsx(
                "absolute inset-0 overflow-hidden",
                "bg-gradient-to-br from-[#FEFCF8] via-[#FAF7F0] to-[#F0E8E1]",
                className
            )}
        >
            <AnimatedGrid />
            <motion.div className="absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2">
                {[0, 1, 2].map((i) => (
                    <motion.div
                        key={i}
                        className={clsx(
                            "absolute inset-0 rounded-full",
                            "border-2 bg-gradient-to-br to-transparent",
                            variantStyles.border[i],
                            variantStyles.gradient
                        )}
                        animate={{
                            rotate: 360,
                            scale: [1, 1.05 + i * 0.05, 1],
                            opacity: [0.6, 0.8, 0.6],
                        }}
                        transition={{
                            duration: 8 + i * 2,
                            repeat: Number.POSITIVE_INFINITY,
                            ease: "easeInOut",
                        }}
                    >
                        <div
                            className={clsx(
                                "absolute inset-0 rounded-full mix-blend-screen",
                                "bg-[radial-gradient(ellipse_at_center,#F97316/15%,transparent_70%)]"
                            )}
                        />
                    </motion.div>
                ))}
            </motion.div>

            <div className="absolute inset-0 [mask-image:radial-gradient(90%_60%_at_50%_50%,#000_40%,transparent)]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,#F97316/20%,transparent_70%)] blur-[120px]" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,#FB923C/10%,transparent)] blur-[80px]" />
            </div>
        </div>
    );
}